import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const CSRF_COOKIE_NAME = 'hd_csrf';

/**
 * Read the `hd_csrf` cookie value synchronously. Returns `undefined` when
 * the cookie is absent (first request after a fresh page load before the
 * bootstrap, or a non-browser environment). Kept in this module to avoid a
 * cycle with `src/shared/api/csrf.ts`, which re-exports the same helper
 * for ergonomic imports from feature code.
 */
export function readCsrfCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const raw = document.cookie;
  if (!raw) return undefined;
  for (const part of raw.split(';')) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');
    if (eq < 0) continue;
    const name = trimmed.slice(0, eq);
    if (name !== CSRF_COOKIE_NAME) continue;
    const value = trimmed.slice(eq + 1);
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  return undefined;
}

// boundary: API response shapes vary per endpoint. The return type is the
// permissive `SafeFetchResult` helper — each endpoint's response is safe to
// index into, and plan 23 will thread proper generics as feature code migrates
// to React Query + typed endpoint helpers.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SafeFetchResult = any;

/**
 * An Error thrown by safeFetch on non-2xx responses. Extends the native Error
 * with the parsed body and status so callers can branch on `err.code` /
 * `err.status` instead of regex-matching messages.
 *
 * Plan 22: the `code` field is what the toast allowlist
 * (`src/shared/errors/messageFor.ts`) keys on.
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly requestId?: string;
  public readonly payload: unknown;
  constructor(
    message: string,
    status: number,
    payload: unknown,
    code?: string,
    requestId?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.payload = payload;
  }
}

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Determine whether a request is a mutation. We have to match the semantic
 * the server uses — `GET`/`HEAD`/`OPTIONS` bypass CSRF; everything else
 * requires the header.
 */
function isMutationMethod(method: string | undefined): boolean {
  if (!method) return false; // default `fetch` verb is GET
  return MUTATION_METHODS.has(method.toUpperCase());
}

/**
 * Merge a single-header override into an existing Headers / init pair. Keeps
 * the caller's casing preferences while guaranteeing the merged header
 * survives on RequestInit.headers (which accepts Headers | string[][] |
 * Record<string,string>).
 */
function withHeader(init: RequestInit | undefined, name: string, value: string): RequestInit {
  const next: RequestInit = { ...(init ?? {}) };
  const existing = next.headers;
  if (existing instanceof Headers) {
    const merged = new Headers(existing);
    merged.set(name, value);
    next.headers = merged;
  } else if (Array.isArray(existing)) {
    next.headers = [...existing, [name, value]];
  } else if (existing && typeof existing === 'object') {
    next.headers = { ...(existing as Record<string, string>), [name]: value };
  } else {
    next.headers = { [name]: value };
  }
  return next;
}

/**
 * Unified fetch wrapper. Plan 22 changes:
 *   - Always attaches cookies (`credentials: 'include'`) so the httpOnly
 *     `session` cookie flows with every request.
 *   - On mutations it reads the `hd_csrf` cookie synchronously and sets
 *     `x-csrf-token`. `providers.tsx` seeds the cookie on app mount via
 *     `useCsrfBootstrap`.
 *   - Non-2xx responses throw `ApiError` with `status`, `code`, `payload`,
 *     `requestId` so callers can surface allowlisted toast copy via
 *     `messageFor(err.code)` instead of dumping the server's message.
 *
 * External URLs (e.g. the Photon geocoder in AuthModal) pass through
 * untouched — we only decorate same-origin / relative paths because the CSRF
 * cookie and session cookie have no meaning to third parties. A URL is
 * treated as "ours" when it begins with `/` (relative) or matches the
 * current origin.
 */
function isSameOriginUrl(url: string): boolean {
  if (url.startsWith('/')) return true;
  if (typeof window === 'undefined') return false;
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Re-seed the `hd_csrf` cookie by hitting `GET /api/csrf-token`. Used by the
 * `csrf_error` auto-recovery path in `safeFetch` so a stale or expired token
 * does not break the UX with a "Your session expired" toast. The endpoint is
 * cheap (constant-time on the server), idempotent, and exempt from CSRF
 * itself, so we can call it freely. `credentials: 'include'` is required so
 * the Set-Cookie response lands on the document. Returns the new token (or
 * undefined if the call failed) so callers can attach it to the retry
 * synchronously without depending on the cookie having been parsed yet.
 */
async function rebootstrapCsrfToken(): Promise<string | undefined> {
  try {
    const res = await fetch('/api/csrf-token', { credentials: 'include' });
    if (!res.ok) return undefined;
    const data = (await res.json()) as { token?: unknown; csrfToken?: unknown };
    const token = typeof data.token === 'string' ? data.token : undefined;
    const alt = typeof data.csrfToken === 'string' ? data.csrfToken : undefined;
    return token ?? alt;
  } catch {
    return undefined;
  }
}

export async function safeFetch(url: string, options?: RequestInit): Promise<SafeFetchResult> {
  return safeFetchInner(url, options, /* allowCsrfRetry */ true);
}

async function safeFetchInner(
  url: string,
  options: RequestInit | undefined,
  allowCsrfRetry: boolean
): Promise<SafeFetchResult> {
  let init: RequestInit = options ?? {};

  // Only decorate same-origin requests — external URLs have no session
  // cookie and a leaked `x-csrf-token` header would be a minor CSRF-defense
  // fingerprint without any upside.
  if (isSameOriginUrl(url)) {
    init = { credentials: 'include', ...init };
    if (isMutationMethod(init.method)) {
      const csrf = readCsrfCookie();
      if (csrf) {
        init = withHeader(init, 'x-csrf-token', csrf);
      }
    }
  }

  try {
    const res = await fetch(url, init);
    const contentType = res.headers.get('content-type');
    const requestId = res.headers.get('x-request-id') ?? undefined;

    if (contentType && contentType.includes('application/json')) {
      const text = await res.text();
      if (!text) {
        if (!res.ok)
          throw new ApiError(
            `HTTP error! status: ${res.status}`,
            res.status,
            null,
            undefined,
            requestId
          );
        return null;
      }
      const data = JSON.parse(text);
      if (!res.ok) {
        // Plan 11 error shape: `{ error: { code, message }, requestId? }`.
        // Legacy shape: `{ error: "string" }`. Decode both without leaking
        // the server's prose into the thrown message — callers use
        // `messageFor(err.code)` to source copy.
        let code: string | undefined;
        let message = `HTTP error! status: ${res.status}`;
        if (data && typeof data === 'object') {
          const errField = (data as { error?: unknown }).error;
          if (errField && typeof errField === 'object') {
            const e = errField as { code?: unknown; message?: unknown };
            if (typeof e.code === 'string') code = e.code;
            if (typeof e.message === 'string') message = e.message;
          } else if (typeof errField === 'string') {
            message = errField;
          } else if (typeof (data as { message?: unknown }).message === 'string') {
            message = (data as { message: string }).message;
          }
        }
        // Auto-recover from a stale or missing CSRF token. The cookie has a
        // 24h max-age and the bootstrap query does not refetch on focus or
        // remount, so a tab left open past that window (or one whose cookie
        // was cleared by ITP / private mode) lands on a 403 the first time
        // the user submits anything. Re-seed the token via GET and retry the
        // original request once — invisible to the caller, no "Please
        // refresh" toast. `allowCsrfRetry` guards against an infinite loop
        // in the pathological case where the second attempt also 403s.
        if (
          allowCsrfRetry &&
          res.status === 403 &&
          code === 'csrf_error' &&
          isSameOriginUrl(url) &&
          isMutationMethod((options ?? {}).method)
        ) {
          const fresh = await rebootstrapCsrfToken();
          if (fresh) {
            const retryInit = withHeader(options ?? {}, 'x-csrf-token', fresh);
            return safeFetchInner(url, retryInit, /* allowCsrfRetry */ false);
          }
        }
        throw new ApiError(
          message,
          res.status,
          data,
          code,
          requestId ?? (data?.requestId as string | undefined)
        );
      }
      return data;
    } else {
      const text = await res.text();
      if (!res.ok) {
        console.error(`Non-JSON error response from ${url}:`, text.substring(0, 200));
        throw new ApiError(
          `Server error (${res.status}): ${text.substring(0, 100)}`,
          res.status,
          text,
          undefined,
          requestId
        );
      }
      return text;
    }
  } catch (err) {
    if (err instanceof ApiError) throw err;
    console.error(`Fetch failed for ${url}:`, err);
    if (err instanceof TypeError && err.message === 'Failed to fetch') {
      throw new ApiError(
        'Network error: Please check your internet connection.',
        0,
        null,
        'network_error'
      );
    }
    throw err;
  }
}
